import { useEffect } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Routes, SyncHook } from '../route-types';

import ProtectedMemberPage from 'react-components/member-page/ProtectedMemberPage';
import {
  selectMemberById,
  selectSelectedMember,
} from 'redux/slices/memberSlice';
import {
  selectMember,
  selectSelectedMemberId,
  selectSelectedTeamMemberId,
  selectTeamMember,
} from 'redux/slices/selectedMemberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import {
  getMemberPagePath,
  useNavigateToMemberPage,
} from '../navigation-hooks';
import { HACK_setGlobalNavigationObjects } from './member-page-routing-hack';

const useSyncHook: SyncHook = () => {
  const dispatch = useAppDispatch();
  const { memberId: routeMemberId, guideId: routeGuideId } = useParams();
  const navigate = useNavigate();
  const reduxGuideId = useAppSelector(selectSelectedTeamMemberId);
  const reduxMemberId = useAppSelector(selectSelectedMemberId);
  const selectedMember = useAppSelector(selectSelectedMember);
  const [searchParams] = useSearchParams();

  const location = useLocation();

  // If the selected member updates in the route, make sure it is reflected in redux.
  useEffect(() => {
    dispatch(selectMember(routeMemberId));
  }, [routeMemberId]);

  // Update redux with the guieId in the route if it is actually the member's guide
  useEffect(() => {
    const reduxGuideDoesNotMatchRouteGuide =
      routeGuideId && routeGuideId !== reduxGuideId;

    if (reduxGuideDoesNotMatchRouteGuide) {
      dispatch(selectTeamMember(routeGuideId));
    }
  }, [routeGuideId, reduxGuideId, selectedMember]);

  // Update the route guideId if it isn't the member's actual guide
  useEffect(() => {
    const guideIdIsSynced = routeGuideId === reduxGuideId;
    const selectedMemberIsLoaded =
      selectedMember && selectedMember.id === reduxMemberId;
    const guideIsNotTheMembersGuide =
      routeGuideId !== selectedMember?.assignedGuideId;

    if (
      guideIdIsSynced &&
      selectedMemberIsLoaded &&
      guideIsNotTheMembersGuide
    ) {
      const url = getMemberPagePath(
        location.pathname,
        selectedMember?.assignedGuideId,
        selectedMember.id
      );

      navigate(
        {
          pathname: url,
          search: `${searchParams}`,
        },
        { replace: true }
      );
    }
  }, [routeGuideId, reduxGuideId, reduxMemberId, selectedMember]);

  // When leaving the member page we should clear the selected member
  useEffect(
    () => () => {
      dispatch(selectMember(null));
    },
    []
  );
};

const OldRouteReroute = () => {
  const { oldMemberId } = useParams();
  const location = useLocation();
  const member = useAppSelector(state =>
    selectMemberById(oldMemberId || '')(state)
  );

  // Try to use the member's actual guide ID if available, otherwise use 'none'
  // The sync hook will correct it to the proper guide ID once the member loads
  const guideId = member?.assignedGuideId || 'none';
  const newRoute = getMemberPagePath(
    location.pathname,
    guideId,
    oldMemberId || ''
  );

  return <Navigate to={newRoute} replace />;
};

const routes: Routes = [
  {
    path: '/members/:oldMemberId',
    element: <OldRouteReroute />,
  },
  {
    path: '/docket/guide/:guideId/member/:memberId',
    element: <ProtectedMemberPage />,
  },
  {
    path: '/member-list/guide/:guideId/member/:memberId',
    element: <ProtectedMemberPage />,
  },
];

const useNavigationHooks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateToMemberPage = useNavigateToMemberPage();

  HACK_setGlobalNavigationObjects({ navigate, location });

  return {
    toMemberPage: (
      guideId: string,
      memberId: string,
      feedItem?: {
        externalType?: string;
        externalId?: string | number;
      },
      needId?: string,
      replace?: boolean,
      showGoals?: boolean
    ) => {
      navigateToMemberPage(
        guideId,
        memberId,
        feedItem,
        needId,
        replace,
        showGoals
      );
    },
  };
};

export const memberPageRouteConfig = {
  routes,
  useSyncHook,
  useNavigationHooks,
};
